import { useEffect, useState } from "react";
import { BrandMark } from "@/components/BrandMark";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function LoginDeviceCheck({ challenge, onVerified, onBack }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [seconds, setSeconds] = useState(challenge.resend_after || 60);
  const [message, setMessage] = useState(challenge.delivery_failed ? "We couldn't send your code. Please wait a moment, then request another code." : "");
  const [deliveryFailed, setDeliveryFailed] = useState(challenge.delivery_failed);
  useEffect(() => {
    const timer = setInterval(() => setSeconds(value => Math.max(0, value - 1)), 1000);
    return () => clearInterval(timer);
  }, []);
  const verify = async event => {
    event.preventDefault();
    setBusy(true); setMessage("");
    try {
      const { data } = await api.post("/auth/device/verify", { code });
      onVerified(data);
    } catch (error) {
      setMessage(error.response?.status === 400 || error.response?.status === 422 ? "That code is invalid or has expired." : "We couldn't verify your code. Please try again.");
    } finally { setBusy(false); }
  };
  const resend = async () => {
    setBusy(true); setMessage("");
    try {
      const { data } = await api.post("/auth/device/resend");
      setSeconds(data.resend_after || 60); setCode(""); setDeliveryFailed(data.delivery_failed);
      setMessage(data.delivery_failed ? "We couldn't send your code. Please try again shortly." : "A new code has been sent. Only the latest code will work.");
    } catch (error) {
      setMessage(error.response?.status === 429 ? "Please wait before requesting another code. If you've requested several, try again later." : "We couldn't send a new code. Try signing in again.");
      if (error.response?.status === 429) setSeconds(60);
    } finally { setBusy(false); }
  };
  return <main className="min-h-dvh bg-[#07111A] text-slate-100 grid place-items-center px-5 py-8" data-testid="login-device-check">
    <section className="w-full max-w-md">
      <BrandMark size={56} />
      <p className="text-xs text-orange-400 font-semibold tracking-widest mt-6">IDENTITY · VERIFICATION REQUIRED</p>
      <h1 className="font-display text-2xl font-bold mt-2">QUICK SECURITY CHECK</h1>
      <p className="mt-4">We haven't seen this device before.</p>
      <p className="text-sm text-slate-300 mt-2">To protect your company, enter the 6-digit security code {deliveryFailed ? "requested for" : "we sent to"}:</p>
      <p className="font-semibold mt-2 break-all">{challenge.masked_email}</p>
      <form onSubmit={verify} className="mt-6 space-y-5">
        <label htmlFor="security-code" className="block text-sm">Security code</label>
        <InputOTP id="security-code" aria-label="Six-digit security code" aria-describedby="code-help code-message" value={code} onChange={value => setCode(value.replace(/\D/g, "").slice(0, 6))} maxLength={6} pattern="[0-9]*" inputMode="numeric" autoComplete="one-time-code" pasteTransformer={text => text.replace(/\D/g, "")} autoFocus disabled={busy}>
          <InputOTPGroup>{Array.from({ length: 6 }, (_, index) => <InputOTPSlot key={index} index={index} className="h-12 w-10 sm:w-12 text-xl border-slate-500" />)}</InputOTPGroup>
        </InputOTP>
        <p id="code-help" className="text-sm text-slate-400">Codes expire after 10 minutes. After verification, this browser is trusted for 90 days. Only continue on a browser you trust.</p>
        <p id="code-message" role="status" className="text-sm text-orange-300">{message}</p>
        <Button type="submit" disabled={busy || code.length !== 6} className="w-full h-12 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white">{busy ? "Please wait…" : "VERIFY & CONTINUE"}</Button>
        <div className="flex flex-wrap justify-between gap-3">
          <button type="button" className="min-h-11 text-sm underline disabled:text-slate-500" disabled={busy || seconds > 0} onClick={resend}>{seconds > 0 ? `Resend code in ${seconds}s` : "Resend code"}</button>
          <button type="button" className="min-h-11 text-sm underline" disabled={busy} onClick={onBack}>Back to sign in</button>
        </div>
      </form>
    </section>
  </main>;
}
